import React, { useEffect, useRef } from "react";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./LiveMap.css";

const TORINO = [45.0703, 7.6869];
const COLOR_HEX = { rider: "#00E676", business: "#F59E0B", customer: "#22D3EE" };

function iconFor(p) {
  const color = p.color || COLOR_HEX[p.type] || "#00E676";
  if (p.type === "rider") {
    return L.divIcon({
      className: "fuma-pin",
      html: `<span class="fuma-rider"><span class="fuma-rider-pulse" style="--c:${color}"></span><span class="fuma-rider-dot" style="--c:${color}"></span></span>`,
      iconSize: [22, 22], iconAnchor: [11, 11],
    });
  }
  return L.divIcon({
    className: "fuma-pin",
    html: `<span style="display:block;width:18px;height:18px;border-radius:50%;background:${color};box-shadow:0 0 0 4px rgba(0,0,0,0.45);border:2px solid #0A0A0C;"></span>`,
    iconSize: [18, 18], iconAnchor: [9, 9],
  });
}

const easeInOut = (t) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t);

function idOf(p, i) { return p.id != null ? String(p.id) : `${p.type || "pt"}-${i}`; }

// Marker gestiti in modo imperativo: quando la posizione di un rider cambia,
// il pin scivola in modo fluido (tween rAF) invece di "saltare" tra i poll.
function AnimatedLayer({ points }) {
  const map = useMap();
  const store = useRef(new Map()); // id -> { marker, raf }

  useEffect(() => {
    const items = points.filter((p) => typeof p.lat === "number" && typeof p.lng === "number").map((p, i) => ({ ...p, _id: idOf(p, i) }));
    const seen = new Set();
    items.forEach((p) => {
      seen.add(p._id);
      let e = store.current.get(p._id);
      if (!e) {
        const marker = L.marker([p.lat, p.lng], { icon: iconFor(p), zIndexOffset: p.type === "rider" ? 1000 : 0 }).addTo(map);
        if (p.label) marker.bindTooltip(p.label, { permanent: p.permanent !== false, direction: "top", offset: [0, -12], className: "fuma-tip" });
        store.current.set(p._id, { marker, raf: null });
        return;
      }
      const cur = e.marker.getLatLng();
      if (Math.abs(cur.lat - p.lat) > 1e-7 || Math.abs(cur.lng - p.lng) > 1e-7) {
        const from = { lat: cur.lat, lng: cur.lng };
        const to = { lat: p.lat, lng: p.lng };
        const start = performance.now();
        const dur = 1500;
        if (e.raf) cancelAnimationFrame(e.raf);
        const step = (now) => {
          const t = Math.min(1, (now - start) / dur);
          const k = easeInOut(t);
          e.marker.setLatLng([from.lat + (to.lat - from.lat) * k, from.lng + (to.lng - from.lng) * k]);
          if (t < 1) e.raf = requestAnimationFrame(step); else e.raf = null;
        };
        e.raf = requestAnimationFrame(step);
      }
    });
    store.current.forEach((e, id) => {
      if (!seen.has(id)) { if (e.raf) cancelAnimationFrame(e.raf); map.removeLayer(e.marker); store.current.delete(id); }
    });
  }, [points, map]);

  useEffect(() => {
    const s = store.current;
    return () => { s.forEach((e) => { if (e.raf) cancelAnimationFrame(e.raf); map.removeLayer(e.marker); }); s.clear(); };
  }, [map]);

  return null;
}

// Inquadra i punti solo quando cambia l'INSIEME (aggiunte/rimozioni), non a ogni
// spostamento: così la mappa non "salta" mentre i rider si muovono.
function FitBounds({ points }) {
  const map = useMap();
  const valid = points.filter((p) => typeof p.lat === "number" && typeof p.lng === "number");
  const ids = valid.map((p, i) => idOf(p, i)).sort().join("|");
  useEffect(() => {
    if (!valid.length) return;
    if (valid.length === 1) map.setView([valid[0].lat, valid[0].lng], 15);
    else map.fitBounds(valid.map((p) => [p.lat, p.lng]), { padding: [40, 40], maxZoom: 16 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids, map]);
  return null;
}

// Percorso animato (rider -> destinazione): polyline neon "marching ants".
function RouteLayer({ route }) {
  const map = useMap();
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) { map.removeLayer(ref.current); ref.current = null; }
    if (route && route.length > 1) {
      ref.current = L.polyline(route, { className: "fuma-route", color: "#00E676", weight: 4, opacity: 0.9 }).addTo(map);
    }
    return () => { if (ref.current) { map.removeLayer(ref.current); ref.current = null; } };
  }, [route, map]);
  return null;
}

export default function LiveMap({ points = [], route = [], className = "", height = 320 }) {
  const valid = points.filter((p) => typeof p.lat === "number" && typeof p.lng === "number");
  const center = valid.length ? [valid[0].lat, valid[0].lng] : TORINO;
  return (
    <div data-testid="live-map" className={`rounded-lg overflow-hidden border border-white/10 ${className}`} style={{ height }}>
      <MapContainer center={center} zoom={13} scrollWheelZoom={false} style={{ height: "100%", width: "100%", background: "#141416" }}>
        <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <RouteLayer route={route} />
        <AnimatedLayer points={points} />
        <FitBounds points={points} />
      </MapContainer>
    </div>
  );
}
